/**
 * Data model + pure helpers for the Create Monthly Challenge wizard's
 * "Schedule" step (FE-2257 / FE-2374).
 *
 * Deliberately authored fresh rather than reusing the Season wizard's
 * `ScheduleValues` / `isScheduleValid`: the two flows diverge on the wire
 * contract (the monthly schedule API isn't settled yet) and on behaviour —
 * its end date/time stay manual even while auto-start is on. Keeping this
 * module self-contained lets the monthly flow evolve without pulling the
 * season flow along with it, and keeps this ticket's diff off the shared
 * `createSeasonData` file so it never collides with the challenges-step work
 * happening in parallel.
 *
 * FE-2374 mirrors the Season wizard's auto-start behaviour: toggling
 * "Auto start after" on anchors the new challenge to the currently ACTIVE
 * monthly challenge, auto-filling (and locking) the start to one minute after
 * that challenge's end and pre-filling an editable 3-day end window.
 */
import dayjs, { type Dayjs } from "dayjs";

import {
  CAMPAIGN_API_STATUS,
  type CampaignApiRecord,
} from "../../../types/gamePass";

/**
 * Step 3 — Schedule values. Dates and times are split so each field maps to
 * its own control in the 2×2 grid from the Figma. `previousSeasonId` is a
 * legacy field retained only for the (PROVISIONAL) update mapper; the auto-start
 * flow no longer authors it from this UI — see `resolveMonthlyAutoStartAnchor`.
 */
export type MonthlyScheduleValues = {
  autoStartAfter: boolean;
  previousSeasonId?: string;
  startDate?: Dayjs;
  startTime?: Dayjs;
  endDate?: Dayjs;
  endTime?: Dayjs;
};

/** Default start time pre-filled per the Figma — 12:00 am. Only the hour /
 *  minute are ever read (composed onto `startDate`), so the date part is
 *  irrelevant. */
export const DEFAULT_START_TIME: Dayjs = dayjs()
  .hour(0)
  .minute(0)
  .second(0)
  .millisecond(0);

/** Default end time pre-filled per the Figma — 11:59 pm. */
export const DEFAULT_END_TIME: Dayjs = dayjs()
  .hour(23)
  .minute(59)
  .second(0)
  .millisecond(0);

export const MONTHLY_SCHEDULE_INITIAL_VALUES: MonthlyScheduleValues = {
  autoStartAfter: false,
  startTime: DEFAULT_START_TIME,
  endTime: DEFAULT_END_TIME,
};

// ─── Auto-start scheduling (FE-2374) ───────────────────────────────────────

/** Gap between the anchor challenge's end and the auto-started challenge's
 *  start. The new challenge begins exactly one minute after the previous one
 *  ends (anchor ends 05/31 11:59pm → auto-start begins 06/01 12:00am) so the
 *  two run back-to-back with no overlap. Mirrors the Season wizard. */
export const MONTHLY_AUTO_START_GAP_MINUTES = 1;

/** Default length pre-filled for the (editable) end date when auto-start is
 *  enabled — start + this many days. The admin can still adjust the end.
 *  Mirrors the Season wizard's `AUTO_START_DEFAULT_DURATION_DAYS`. */
export const MONTHLY_AUTO_START_DEFAULT_DURATION_DAYS = 3;

/** The predecessor an auto-started monthly challenge chains after — the
 *  currently ACTIVE monthly challenge (campaign). `end` drives the auto-filled
 *  (and locked) start, one minute later. */
export type MonthlyAutoStartAnchor = {
  /** The active campaign's id. `null` only if the record carries no id
   *  (defensive — a real record always has one). */
  id: string | null;
  /** The active campaign's end instant → the new challenge starts one minute
   *  after. */
  end: Dayjs;
};

/**
 * Resolve the predecessor a new auto-started monthly challenge should chain
 * after: the currently ACTIVE monthly challenge. The new draft is filled to
 * start one minute after this campaign's `end_date`. Returns `null` when no
 * active campaign exists (nothing to chain after). The (defensive) "latest end
 * wins" tie-break handles the case of more than one concurrent active campaign
 * — unlike Seasons, the campaign backend allows several at once.
 *
 * `excludeCampaignId` skips the draft currently being authored so it can never
 * anchor to itself. Pure (no React) so it can be unit-tested in isolation —
 * mirrors the Season wizard's `resolveAutoStartAnchor`.
 */
export const resolveMonthlyAutoStartAnchor = (
  campaigns: ReadonlyArray<CampaignApiRecord>,
  excludeCampaignId?: string | null,
): MonthlyAutoStartAnchor | null => {
  let anchor: MonthlyAutoStartAnchor | null = null;
  for (const record of campaigns) {
    const recordId = record.campaign_id ?? record.id ?? null;
    if (excludeCampaignId && recordId === excludeCampaignId) continue;
    if (record.status !== CAMPAIGN_API_STATUS.ACTIVE) continue;
    if (!record.end_date) continue;
    const end = dayjs(record.end_date);
    if (!end.isValid()) continue;
    if (!anchor || end.isAfter(anchor.end)) anchor = { id: recordId, end };
  }
  return anchor;
};

/** The auto-start date/time slice derived from an anchor end instant. */
export type MonthlyAutoStartScheduleFill = Pick<
  MonthlyScheduleValues,
  "startDate" | "startTime" | "endDate" | "endTime"
>;

/**
 * Build the auto-start date/time fill from the anchor challenge's end instant:
 * the new challenge starts exactly `MONTHLY_AUTO_START_GAP_MINUTES` after the
 * anchor ends and defaults to a `MONTHLY_AUTO_START_DEFAULT_DURATION_DAYS`-long
 * window. `startDate` / `startTime` share the (locked) start instant; `endDate`
 * / `endTime` share the (editable) end instant. Returns all-`undefined` when
 * there is no valid anchor so the caller can leave the grid empty until the
 * campaigns list resolves. Mirrors the Season wizard's `buildAutoStartSchedule`.
 */
export const buildMonthlyAutoStartSchedule = (
  anchorEnd: Dayjs | null | undefined,
): MonthlyAutoStartScheduleFill => {
  if (!anchorEnd || !anchorEnd.isValid()) {
    return {
      startDate: undefined,
      startTime: undefined,
      endDate: undefined,
      endTime: undefined,
    };
  }
  const start = anchorEnd
    .add(MONTHLY_AUTO_START_GAP_MINUTES, "minute")
    .second(0)
    .millisecond(0);
  const end = start.add(MONTHLY_AUTO_START_DEFAULT_DURATION_DAYS, "day");
  return { startDate: start, startTime: start, endDate: end, endTime: end };
};

/**
 * Step 3 is valid when all four date/time fields are filled AND the composed
 * end instant is strictly after the composed start. Composing date + time (not
 * date alone) means a same-day window with an end time earlier than the start
 * time is correctly rejected. Unlike the Season wizard, auto-start does NOT
 * short-circuit validity: the monthly end date/time are authored manually even
 * while auto-start fills the start, so they're still required.
 */
export const isMonthlyScheduleValid = (
  values?: Partial<MonthlyScheduleValues>,
): boolean => {
  if (!values) return false;
  if (
    !values.startDate ||
    !values.startTime ||
    !values.endDate ||
    !values.endTime
  ) {
    return false;
  }
  const start = values.startDate
    .startOf("day")
    .hour(values.startTime.hour())
    .minute(values.startTime.minute());
  const end = values.endDate
    .startOf("day")
    .hour(values.endTime.hour())
    .minute(values.endTime.minute());
  return end.isAfter(start);
};
